import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Calendar from './Calendar';
import { formatDateWithWeekdayIST, parseFromDatabase, getCurrentDateIST } from '../utils/timezone';
import { API_BASE_URL } from '../config/api';
import AttendanceLauncher from './AttendanceMaster/AttendanceLauncher';
import AdminStreamControl from './LiveStream/AdminStreamControl';
import BulkDataManager from './BulkDataManager';
import DiaryManagerPanel from './DiaryManagerPanel';

function AdminDashboard({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', role: 'admin1' });
  const [newPrincipal, setNewPrincipal] = useState({ name: '', email: '', password: '', role: 'principal' });
  const [systemSettings, setSystemSettings] = useState({ 
    theme: 'default', 
    schoolName: 'The Rising School',
    serverStatus: 'Online',
    dbStatus: 'Connected'
  });
  const [academicYear, setAcademicYear] = useState({ year: '2024-25', startMonth: 'April', endMonth: 'March' });

  useEffect(() => {
    fetchUsers();
    fetchAcademicYear();
  }, []);
  
  const fetchAcademicYear = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/academic-year`);
      if (response.data) setAcademicYear(response.data);
    } catch (error) {
      console.error('Error fetching academic year:', error);
    }
  };
  
  const saveAcademicYear = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/academic-year`, academicYear);
      alert('Academic year settings saved successfully!');
    } catch (error) {
      console.error('Error saving academic year:', error);
      alert('Error saving academic year');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/users', newAdmin);
      setNewAdmin({ name: '', email: '', password: '', role: 'admin1' });
      fetchUsers();
      alert('Admin Level 1 created successfully!');
    } catch (error) {
      console.error('Error creating admin:', error);
    }
  };

  const handleAddPrincipal = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/users', newPrincipal);
      setNewPrincipal({ name: '', email: '', password: '', role: 'principal' });
      fetchUsers();
      alert('Principal created successfully!');
    } catch (error) {
      console.error('Error creating principal:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await axios.delete(`/api/users/${userId}`);
        fetchUsers();
        alert('User deleted successfully!');
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const overrideUserData = async (userId, newData) => {
    try {
      await axios.put(`/api/users/${userId}`, newData);
      fetchUsers();
      alert('User data overridden successfully!');
    } catch (error) {
      console.error('Error overriding user data:', error);
    }
  };

  const emergencyOverride = () => {
    const password = prompt('Enter admin password for emergency override:');
    if (password === 'admin123') {
      alert('Emergency override mode activated! Full system access granted.');
    } else {
      alert('Invalid password!');
    }
  };

  const backupData = () => {
    alert('Full system backup initiated! This may take a few minutes.');
  };

  const viewLogs = () => {
    alert('System logs opened - showing last 1000 entries');
  };

  const viewAnalytics = () => {
    alert('Comprehensive system analytics dashboard opened!');
  };

  const managePermissions = () => {
    alert('Permission management panel opened!');
  };

  const restartServices = () => {
    if (window.confirm('Restart all services? This will temporarily disconnect users.')) {
      alert('Services restarting... System will be back online in 30 seconds.');
    }
  };

  const maintenanceMode = () => {
    if (window.confirm('Enable maintenance mode? This will block all user access.')) {
      alert('Maintenance mode enabled. Only admins can access the system.');
    }
  };

  const saveSettings = () => {
    alert('Global system settings saved successfully!');
  };

  const editUser = async (user) => {
    const newName = prompt(`Edit name for ${user.name}:`, user.name);
    const newEmail = prompt(`Edit email for ${user.name}:`, user.email);
    const newPassword = prompt(`Edit password for ${user.name} (leave blank to keep current):`, '');
    
    if (newName && newEmail && newName.trim() && newEmail.trim()) {
      try {
        const updateData = { name: newName.trim(), email: newEmail.trim() };
        if (newPassword && newPassword.trim()) {
          updateData.password = newPassword.trim();
        }
        await axios.put(`/api/users/${user._id}`, updateData);
        fetchUsers();
        alert(`${user.role === 'principal' ? 'Principal' : 'Admin Level 1'} updated successfully!`);
      } catch (error) {
        console.error('Error updating user:', error);
        alert('Error updating user: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  return (
    <div className="dashboard admin-theme">
      <div style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', borderBottom: '2px solid #dee2e6' }}>
        <Calendar userRole="admin" userClass="all" userName={user.name} />
      </div>
      
      <div className="header">
        <div className="profile-section">
          <div className="profile-pic">👑</div>
          <div>
            <h3>{user.name}</h3>
            <p>Top-Level Administrator</p>
          </div>
        </div>
        <button className="logout-btn" onClick={onLogout}>Logout</button>
      </div>

      <div className="dashboard-grid">
        <AttendanceLauncher user={user} />
        <div id="bulk-data-manager" style={{ gridColumn: '1 / -1' }}>
          <BulkDataManager user={user} />
        </div>
        <div id="diary-manager" style={{ gridColumn: '1 / -1' }}>
          <DiaryManagerPanel user={user} />
        </div>
        {['principal', 'admin'].includes(user?.role) && (
          <div id="admin-stream-control" style={{ gridColumn: '1 / -1' }}>
            <AdminStreamControl />
          </div>
        )}
        <div className="card">
          <h3>🎓 Create Principal</h3>
          <form onSubmit={handleAddPrincipal}>
            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                value={newPrincipal.name}
                onChange={(e) => setNewPrincipal({...newPrincipal, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                value={newPrincipal.email}
                onChange={(e) => setNewPrincipal({...newPrincipal, email: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                value={newPrincipal.password}
                onChange={(e) => setNewPrincipal({...newPrincipal, password: e.target.value})}
                required
              />
            </div>
            <button type="submit" className="btn">Create Principal</button>
          </form>
        </div>

        <div className="card">
          <h3>👑 Create Admin Level 1</h3>
          <form onSubmit={handleAddAdmin}>
            <div className="form-group">
              <label>Name:</label>
              <input
                type="text"
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Password:</label>
              <input
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                required
              />
            </div>
            <button type="submit" className="btn">Create Admin Level 1</button>
          </form>
        </div>

        <div className="card">
          <h3>🎓 Principal Management</h3>
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {users.filter(u => u.role === 'principal').map(principal => (
              <div key={principal._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                <div>
                  <strong>{principal.name}</strong>
                  <p>{principal.email}</p>
                  <small>Created: {parseFromDatabase(principal.createdAt, { month: 'short', day: 'numeric' })}</small>
                </div>
                <div>
                  <button className="btn" style={{ fontSize: '12px', padding: '4px 8px' }} onClick={() => editUser(principal)}>Edit</button>
                  <button className="btn" style={{ background: '#dc3545', fontSize: '12px', padding: '4px 8px' }} onClick={() => deleteUser(principal._id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
          <p><strong>Total Principals: {users.filter(u => u.role === 'principal').length}</strong></p>
        </div>

        <div className="card">
          <h3>🛠️ Admin Level 1 Management</h3>
          <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
            {users.filter(u => u.role === 'admin1').map(admin => (
              <div key={admin._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
                <div>
                  <strong>{admin.name}</strong>
                  <p>{admin.email}</p>
                  <small>Created: {parseFromDatabase(admin.createdAt, { month: 'short', day: 'numeric' })}</small>
                </div>
                <div>
                  <button className="btn" style={{ fontSize: '12px', padding: '4px 8px' }} onClick={() => editUser(admin)}>Edit</button>
                  <button className="btn" style={{ background: '#dc3545', fontSize: '12px', padding: '4px 8px' }} onClick={() => deleteUser(admin._id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
          <p><strong>Total Admin Level 1: {users.filter(u => u.role === 'admin1').length}</strong></p>
        </div>

        <div className="card">
          <h3>🔧 Complete System Override</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Override Capabilities:</strong></p>
            <div style={{ fontSize: '14px' }}>
              <p>✅ Edit any user profile</p>
              <p>✅ Override class assignments</p>
              <p>✅ Modify attendance records</p>
              <p>✅ Edit content and materials</p>
              <p>✅ Override system settings</p>
            </div>
          </div>
          <button className="btn" style={{ background: '#dc3545' }} onClick={emergencyOverride}>Emergency Override Mode</button>
          <button className="btn" onClick={() => alert('Bulk operations panel opened!')}>Bulk Data Operations</button>
        </div>

        <div className="card">
          <h3>💾 Complete Data Management</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Database Operations:</strong></p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
              <div style={{ padding: '8px', background: '#d4edda', borderRadius: '4px' }}>
                <strong>Users:</strong> {users.length}
              </div>
              <div style={{ padding: '8px', background: '#cce5ff', borderRadius: '4px' }}>
                <strong>Classes:</strong> 6
              </div>
              <div style={{ padding: '8px', background: '#fff3cd', borderRadius: '4px' }}>
                <strong>Content:</strong> 45
              </div>
              <div style={{ padding: '8px', background: '#f8d7da', borderRadius: '4px' }}>
                <strong>Records:</strong> 1,250
              </div>
            </div>
          </div>
          <button className="btn" onClick={backupData}>Full System Backup</button>
          <button className="btn" onClick={() => alert('Data import/export wizard opened!')}>Import/Export Data</button>
          <button className="btn" onClick={() => alert('Database maintenance completed!')}>Database Maintenance</button>
        </div>

        <div className="card">
          <h3>📈 System Analytics & Logs</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>System Performance:</strong></p>
            <div style={{ fontSize: '14px' }}>
              <p>Server Uptime: 99.8%</p>
              <p>Active Sessions: 45</p>
              <p>Daily Logins: 156</p>
              <p>System Load: 23%</p>
              <p>Database Size: 2.3 GB</p>
            </div>
          </div>
          <button className="btn" onClick={viewLogs}>View System Logs</button>
          <button className="btn" onClick={viewAnalytics}>Performance Analytics</button>
          <button className="btn" onClick={() => alert('User activity reports generated!')}>User Activity Reports</button>
        </div>

        <div className="card">
          <h3>🔐 Access Control Management</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Permission Matrix:</strong></p>
            <div style={{ fontSize: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '4px' }}>Role</th>
                  <th style={{ border: '1px solid #ddd', padding: '4px' }}>Users</th>
                  <th style={{ border: '1px solid #ddd', padding: '4px' }}>Access Level</th>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Student</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>{users.filter(u => u.role === 'student').length}</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Read Only</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Teacher</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>{users.filter(u => u.role === 'teacher').length}</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Class Management</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Admin L1</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>{users.filter(u => u.role === 'admin1').length}</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>User Management</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Principal</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>{users.filter(u => u.role === 'principal').length}</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Admin Level</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Top Admin</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>1</td>
                  <td style={{ border: '1px solid #ddd', padding: '4px' }}>Full Control</td>
                </tr>
              </table>
            </div>
          </div>
          <button className="btn" onClick={managePermissions}>Modify Permissions</button>
          <button className="btn" onClick={() => alert('Security audit completed - no issues found!')}>Security Audit</button>
        </div>

        <div className="card">
          <h3>🖥️ Server Management</h3>
          <div style={{ marginBottom: '15px' }}>
            <p><strong>Server Status:</strong></p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Web Server:</span>
              <span style={{ color: 'green' }}>● Online</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Database:</span>
              <span style={{ color: 'green' }}>● Connected</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>File Storage:</span>
              <span style={{ color: 'green' }}>● Available</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Backup Service:</span>
              <span style={{ color: 'orange' }}>● Scheduled</span>
            </div>
          </div>
          <button className="btn" style={{ background: '#28a745' }} onClick={restartServices}>Restart Services</button>
          <button className="btn" style={{ background: '#ffc107' }} onClick={maintenanceMode}>Maintenance Mode</button>
          <button className="btn" style={{ background: '#dc3545' }} onClick={() => window.confirm('Emergency shutdown? This will stop all services!') && alert('System shutdown initiated!')}>Emergency Shutdown</button>
        </div>

        <div className="card">
          <h3>⚙️ System Settings</h3>
          <div className="form-group">
            <label>School Name:</label>
            <input
              type="text"
              value={systemSettings.schoolName}
              onChange={(e) => setSystemSettings({...systemSettings, schoolName: e.target.value})}
            />
          </div>
          <div className="form-group">
            <label>System Theme:</label>
            <select
              value={systemSettings.theme}
              onChange={(e) => setSystemSettings({...systemSettings, theme: e.target.value})}
            >
              <option value="default">Default</option>
              <option value="dark">Dark Mode</option>
              <option value="light">Light Mode</option>
              <option value="high-contrast">High Contrast</option>
            </select>
          </div>
          <div className="form-group">
            <label>Academic Year:</label>
            <input
              type="text"
              value={academicYear.year}
              onChange={(e) => setAcademicYear({...academicYear, year: e.target.value})}
              placeholder="e.g., 2024-25"
            />
          </div>
          <div className="form-group">
            <label>Start Month:</label>
            <select
              value={academicYear.startMonth}
              onChange={(e) => setAcademicYear({...academicYear, startMonth: e.target.value})}
            >
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>End Month:</label>
            <select
              value={academicYear.endMonth}
              onChange={(e) => setAcademicYear({...academicYear, endMonth: e.target.value})}
            >
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
          <button className="btn" onClick={saveSettings}>Save Global Settings</button>
          <button className="btn" onClick={saveAcademicYear} style={{ background: '#10b981' }}>Save Academic Year</button>
          <button className="btn" onClick={() => window.confirm('Reset all settings to default?') && alert('Settings reset to default values!')}>Reset to Defaults</button>
        </div>


      </div>
    </div>
  );
}

export default AdminDashboard;